<template>
  <div class="settings-container">
    <div class="settings-hub-view">
      <!-- Search Bar -->
      <b-input-group class="search-input-group mb-1">
        <b-input-group-prepend
          is-text
          class="bg-white border-right-0"
        >
          <feather-icon
            icon="SearchIcon"
            class="text-muted"
          />
        </b-input-group-prepend>
        <b-form-input
          v-model="searchQuery"
          placeholder="Search settings..."
          class="border-left-0 shadow-none pl-0 search-field"
        />
      </b-input-group>

      <!-- Settings Cards Grid -->
      <b-row>
        <b-col
          v-for="category in filteredCategories"
          :id="category.id"
          :key="category.id"
          cols="12"
          lg="6"
          class="mb-2"
        >
          <!-- Added conditional class: 'highlighted-category-card' -->
          <b-card
            class="h-100 category-card shadow-sm border-0"
            :class="{ 'highlighted-category-card': category.id === highlightedCategoryId }"
          >
            <!-- Category Header -->
            <div class="mb-1">
              <h4 class="font-weight-bolder category-title">
                {{ category.title }}
              </h4>
              <p class="text-secondary medium mb-0">
                {{ category.description }}
              </p>
            </div>

            <!-- Category Settings Buttons Grid -->
            <b-row class="px-50">
              <b-col
                v-for="item in category.items"
                :key="item.key"
                cols="12"
                md="6"
                class="p-50"
              >
                <div
                  class="setting-item-tile d-flex align-items-center justify-content-between p-1 rounded cursor-pointer"
                  @click="selectSetting(item)"
                >
                  <!-- Title & Icon Container -->
                  <div class="d-flex align-items-center min-w-0 mr-50">
                    <div class="setting-icon-wrapper d-flex align-items-center justify-content-center rounded flex-shrink-0 mr-1">
                      <feather-icon
                        :icon="item.icon"
                        size="18"
                      />
                    </div>
                    <span class="text-dark setting-title">
                      {{ item.title }}
                    </span>
                  </div>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="16"
                    class="text-secondary flex-shrink-0 ml-auto"
                  />
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- Empty Search State -->
      <div
        v-if="filteredCategories.length === 0"
        class="text-center py-5"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="40"
          class="text-muted mb-2"
        />
        <h5 class="text-muted">
          No settings found matching "{{ searchQuery }}"
        </h5>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BInputGroup, BInputGroupPrepend, VBTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import store from '@/store'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
  },
  data() {
    return {
      store,
      aiModuleEnabled: false,
      searchQuery: '',
      categories: [
        {
          id: 'general-branding',
          title: 'General & Branding',
          description: 'Manage business identity, system preferences and platform-wide settings.',
          items: [
            {
              key: 'business-profile',
              title: 'Business Profile',
              icon: 'UsersIcon',
              routeName: 'business-profile',
              permissions: ['business-profile-manage'],
            },
            {
              key: 'notification',
              title: 'Notification',
              icon: 'BellIcon',
              routeName: 'notification-setting',
              permissions: [
                'create-merchant-notification',
                'edit-status-notification',
                'edit-reminder-notification',
                'edit-finance-notification',
                'edit-other-notification',
              ],
            },
            {
              key: 'general',
              title: 'General Settings',
              icon: 'ToolIcon',
              routeName: 'general-settings',
            },
            {
              key: 'terms',
              title: 'Terms & Conditions',
              icon: 'CheckCircleIcon',
              routeName: 'terms-and-conditions',
            },
            {
              key: 'theme',
              title: 'Theme Settings',
              icon: 'ApertureIcon',
              routeName: 'theme-settings',
              permissions: ['web-theme-setting', 'mobile-theme-setting'],
            },
            {
              key: 'approval',
              title: 'Approval Settings',
              icon: 'CheckCircleIcon',
              routeName: 'approval-settings',
              permissions: ['view-profile-update-approval'],
            },
            {
              key: 'ai-settings',
              title: 'AI Settings',
              icon: 'StarIcon',
              routeName: 'ai-settings',
              permissions: ['show-ai-setting'],
              requiresAiModule: true,
            },
          ],
        },
        {
          id: 'order-handling-waybill',
          title: 'Order Handling & Waybill Settings',
          description: 'Manage waybill, tracking, volumetric weight and print layouts.',
          items: [
            {
              key: 'waybill',
              title: 'Waybill Management',
              icon: 'FileMinusIcon',
              routeName: 'waybill-management',
              permissions: ['show-waybill-setting'],
            },
            {
              key: 'volumetric-weight',
              title: 'Volumetric Weight Settings',
              icon: 'DivideIcon',
              routeName: 'volumetric-weight',
            },
            {
              key: 'tracking',
              title: 'Tracking Settings',
              icon: 'MonitorIcon',
              routeName: 'tracking-settings',
              permissions: ['tracking-page-image', 'show-edit-additional-tracking-details'],
            },
            {
              key: 'print-layouts',
              title: 'Print Layouts',
              icon: 'PrinterIcon',
              routeName: 'print-layouts',
              permissions: ['edit-waybill-layout'],
            },
          ],
        },
        {
          id: 'order-management',
          title: 'Order Management',
          description: 'Configure order lifecycle, statuses and cancellation rules.',
          items: [
            {
              key: 'orders',
              title: 'Orders',
              icon: 'BoxIcon',
              routeName: 'orders-settings',
              permissions: ['edit-order-setting'],
            },
            {
              key: 'auto-cancel',
              title: 'Auto Order Cancellation Settings',
              icon: 'XCircleIcon',
              routeName: 'auto-order-cancel',
              permissions: ['manage-auto-order-cancellation-setting'],
            },
            {
              key: 'statuses',
              title: 'Statuses',
              icon: 'OctagonIcon',
              routeName: 'statuses-settings',
              permissions: ['edit-finance-status-setting'],
            },
          ],
        },
        {
          id: 'finance-payments',
          title: 'Finance & Payments',
          description: 'Manage financial preferences, taxes and payment methods.',
          items: [
            {
              key: 'finance',
              title: 'Finance',
              icon: 'DollarSignIcon',
              routeName: 'finance-settings',
              permissions: ['edit-finance-setting'],
            },
            {
              key: 'payment-method',
              title: 'Payment Method',
              icon: 'CreditCardIcon',
              routeName: 'payment-methods',
              permissions: ['show-payment-setting'],
            },
            {
              key: 'tax-settings',
              title: 'Tax Settings',
              icon: 'PercentIcon',
              routeName: 'tax-settings',
              permissions: ['view-tax'],
            },
          ],
        },
        {
          id: 'access-security',
          title: 'Access & Security',
          description: 'Manage users, merchants, roles and security preferences.',
          items: [
            {
              key: 'staff-settings',
              title: 'Staff Settings',
              icon: 'UserIcon',
              routeName: 'staff-settings',
              permissions: ['show-staff-setting'],
            },
            {
              key: 'security-privacy',
              title: 'Security and Privacy Settings',
              icon: 'LockIcon',
              routeName: 'change-password',
              permissions: ['password-rest'],
            },
            {
              key: 'merchant-settings',
              title: 'Merchant Settings',
              icon: 'BriefcaseIcon',
              routeName: 'merchant-settings',
              permissions: ['show-merchant-setting'],
            },
            {
              key: 'merchant-registration-settings',
              title: 'Merchant Registration Settings',
              icon: 'ListIcon',
              routeName: 'merchant-registration-settings',
            },
            {
              key: 'merchant-agreement-settings',
              title: 'Merchant Agreement Settings',
              icon: 'FileTextIcon',
              routeName: 'merchant-agreement-settings',
              permissions: ['show-merchant-agreement-setting'],
            },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['currentUser']),

    highlightedCategoryId() {
      return this.$route.query.category || null
    },

    filteredCategories() {
      const query = this.searchQuery.toLowerCase().trim()

      return this.categories
        .map(category => {
          const visibleItems = category.items.filter(item => {
            const hasPerm = this.checkPermission(item.permissions)
            if (!hasPerm) return false

            if (item.requiresAiModule && !this.aiModuleEnabled) return false

            if (query && !item.title.toLowerCase().includes(query)) return false

            return true
          })

          return {
            ...category,
            items: visibleItems,
          }
        })
        .filter(category => category.items.length > 0)
    },
  },
  mounted() {
    this.fetchModules()
    this.scrollToHighlightedCategory()
  },
  methods: {
    checkPermission(permissions) {
      if (!permissions || permissions.length === 0) return true
      const userPermissions = this.store.getters.permissions || []
      return permissions.some(p => userPermissions.includes(p))
    },

    scrollToHighlightedCategory() {
      const categoryId = this.$route.query.category
      if (categoryId) {
        this.$nextTick(() => {
          const el = document.getElementById(categoryId)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      }
    },

    async fetchModules() {
      try {
        const data = JSON.parse(localStorage.getItem('module_data'))
        if (Array.isArray(data)) {
          const aiSettingModule = data.find(n => n.name === 'AI Setting')
          this.aiModuleEnabled = aiSettingModule ? aiSettingModule.is_active : false
        }
      } catch (e) {
        if (this.convertAndNotifyError) {
          this.convertAndNotifyError(e)
        }
      }
    },

    selectSetting(item) {
      if (item && item.routeName) {
        this.$router.push({ name: item.routeName })
      }
    },
  },
}
</script>

<style scoped>
.search-input-group {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.search-field:focus {
  box-shadow: none !important;
}

.category-card {
  border-radius: 8px !important;
  background-color: #ffffff;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

/* Highlighted shadow and subtle border effect */
.highlighted-category-card {
  box-shadow: 0 0 0 3px rgba(44, 107, 239, 0.30) !important;
  border: 1.5px solid #26CBEF !important;
}

.min-w-0 {
  min-width: 0;
}

.setting-item-tile {
  background-color: #f8f9fa;
  border: 1px solid #ebedf0;
  transition: all 0.2s ease-in-out;
}

.setting-item-tile:hover {
  background-color: #f1f3f5;
  border-color: #dbe2e8;
  transform: translateY(-1px);
}

.setting-icon-wrapper {
  width: 30px;
  height: 30px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  color: #5e5873;
  flex-shrink: 0;
}

.setting-title {
  font-size: 0.95rem;
  font-weight: 700;
}

.category-title {
  color: #000000;
}
</style>
